import React, { useState } from 'react';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import { 
  Edit3,
  Plus,
  Trash2,
  Save,
  Eye,
  AlertCircle,
  CheckCircle,
  Calendar,
  FileText,
  Target
} from 'lucide-react';

const AssignmentEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    const validation = validateContent(updatedContent, 'assignment');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'assignment');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const addRubricCriterion = () => {
    const newCriterion = {
      criterion: 'Nouveau critère',
      points: 10,
      description: 'Description du critère d\'évaluation'
    };
    
    const updatedRubric = [...(content.rubric || []), newCriterion];
    handleFieldChange('rubric', updatedRubric);
  };

  const updateRubricCriterion = (index, updates) => {
    const updatedRubric = (content.rubric || []).map((criterion, i) =>
      i === index ? { ...criterion, ...updates } : criterion
    );
    handleFieldChange('rubric', updatedRubric);
  };

  const removeRubricCriterion = (index) => {
    const updatedRubric = (content.rubric || []).filter((_, i) => i !== index);
    handleFieldChange('rubric', updatedRubric);
  };

  const getTotalPoints = () => {
    return (content.rubric || []).reduce((total, criterion) => total + (criterion.points || 0), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Edit3 className="w-6 h-6 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur de Devoir
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(content)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="space-y-6">
          {/* Informations générales */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Informations du devoir
            </h3>
            
            <div className="space-y-4">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Titre du devoir *
                </label>
                <input
                  type="text"
                  value={content.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="Ex: Projet final - Application web"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={content.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Décrivez l'objectif de ce devoir..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                />
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Instructions détaillées *
                </label>
                <textarea
                  value={content.instructions || ''}
                  onChange={(e) => handleFieldChange('instructions', e.target.value)}
                  placeholder="Donnez des instructions claires pour réaliser le devoir..."
                  rows="6"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                />
              </div>

              {/* Date limite */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date limite de rendu
                </label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={formatDate(content.dueDate)}
                    onChange={(e) => handleFieldChange('dueDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Points totaux */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Points totaux
                </label>
                <input
                  type="number"
                  value={content.maxPoints || 100}
                  onChange={(e) => handleFieldChange('maxPoints', parseInt(e.target.value) || 100)}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Grille d'évaluation */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Grille d'évaluation
              </h3>
              <button
                onClick={addRubricCriterion}
                className="flex items-center gap-2 px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Ajouter un critère
              </button>
            </div>

            <div className="space-y-3">
              {(content.rubric || []).map((criterion, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Critère {index + 1}
                    </h4>
                    <button
                      onClick={() => removeRubricCriterion(index)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nom du critère
                      </label>
                      <input
                        type="text"
                        value={criterion.criterion}
                        onChange={(e) => updateRubricCriterion(index, { criterion: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Points
                      </label>
                      <input
                        type="number"
                        value={criterion.points}
                        onChange={(e) => updateRubricCriterion(index, { points: parseInt(e.target.value) || 0 })}
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={criterion.description}
                        onChange={(e) => updateRubricCriterion(index, { description: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Message si pas de critères */}
            {(content.rubric || []).length === 0 && (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Target className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  Aucun critère d'évaluation défini
                </p>
                <button
                  onClick={addRubricCriterion}
                  className="flex items-center gap-2 px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mx-auto text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un critère
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Aperçu */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Aperçu du devoir
          </h3>
          
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {content.title || 'Titre du devoir'}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {content.description || 'Description du devoir'}
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <FileText className="w-4 h-4" />
                <span>Instructions détaillées fournies</span>
              </div>
              
              {content.dueDate && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>Échéance : {new Date(content.dueDate).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Target className="w-4 h-4" />
                <span>Points totaux : {content.maxPoints || 100}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-4 h-4" />
                <span>{(content.rubric || []).length} critères d'évaluation</span>
              </div>
            </div>
          </div>

          {/* Résumé de la grille */}
          {(content.rubric || []).length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                Résumé de la grille d'évaluation
              </h4>
              <div className="space-y-1">
                {(content.rubric || []).map((criterion, index) => (
                  <div key={index} className="flex items-center justify-between text-xs">
                    <span className="text-blue-800 dark:text-blue-200">
                      {criterion.criterion}
                    </span>
                    <span className="font-medium text-blue-900 dark:text-blue-100">
                      {criterion.points} pts
                    </span>
                  </div>
                ))}
                <div className="border-t border-blue-200 dark:border-blue-800 pt-1 mt-2">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="text-blue-900 dark:text-blue-100">Total</span>
                    <span className="text-blue-900 dark:text-blue-100">
                      {getTotalPoints()} pts
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentEditor;
